import { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import '@/styles/site-office.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const bare = axios.create();

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US',
  { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const abs = (u) => (u && u.startsWith('/api') ? `${API}${u.replace(/^\/api/, '')}` : u);

/**
 * DP275-P4a — the scanned page.
 *
 * What a crew member gets from the code on the wall: today's drawings and spec
 * sheets, nothing to log into, nothing to edit, no navigation to get lost in.
 */
export default function FieldSheetPage() {
  const { code } = useParams();
  const [sheet, setSheet] = useState(null);
  const [error, setError] = useState('');
  const [specs, setSpecs] = useState(null);

  useEffect(() => {
    let alive = true;
    bare.get(`${API}/field-sheets/${code}`)
      .then((r) => { if (alive) setSheet(r.data); })
      .catch(() => { if (alive) setError('This code is not in use.'); });
    return () => { alive = false; };
  }, [code]);

  const openSpecs = async (row) => {
    try {
      const r = await bare.get(abs(row.url));
      setSpecs({ title: `${row.prefix} — ${row.title}`, items: r.data?.items || [] });
    } catch { setSpecs({ title: row.prefix, items: [] }); }
  };

  if (error) {
    return (
      <div className="kso-wrap">
        <div className="kfs-page" data-testid="field-sheet-unknown">
          <div className="kfs-hold">This code is not in use.</div>
        </div>
      </div>
    );
  }
  if (!sheet) return <div className="kso-wrap"><p className="kso-hint">Loading…</p></div>;

  const head = (
    <div className="kfs-page__hd">
      <span className="kfs-page__k">{sheet.firm} · Field sheet</span>
      <span className="kfs-page__t">{sheet.project} · {sheet.name}</span>
      {sheet.state === 'live' ? (
        <span className="kfs-page__u">Updated {fmtDate(sheet.updated_at)} · rev {sheet.rev} · this sheet is current</span>
      ) : null}
    </div>
  );

  if (sheet.state !== 'live') {
    return (
      <div className="kso-wrap">
        <div className="kfs-page" data-testid={`field-sheet-${sheet.state}`}>
          {head}
          <div className="kfs-hold">
            {sheet.firm} · {sheet.project} · {sheet.name} — this sheet is{' '}
            {sheet.state === 'paused' ? 'paused' : 'no longer in use'}. Ask your foreman.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="kso-wrap">
      <div className="kfs-page" data-testid="field-sheet-live">
        {head}
        <div className="kfs-page__bd">
          <div className="kfs-group">Drawings</div>
          {sheet.drawings.map((d) => (
            <div className="kso-row" key={d.id}>
              <div className="t"><b>{d.label}</b></div>
              <a className="kso-btn" href={abs(d.url)} target="_blank" rel="noreferrer"
                data-testid={`field-sheet-drawing-${d.id}`}>Open</a>
            </div>
          ))}
          {sheet.drawings.length ? null : <p className="kso-hint">No drawings on this sheet.</p>}
          <div className="kfs-group">Spec sheets</div>
          {sheet.specs.map((s) => (
            <div className="kso-row" key={s.prefix}>
              <div className="t"><b>{s.prefix}</b> — {s.title} · {s.count} items</div>
              <button type="button" className="kso-btn" onClick={() => openSpecs(s)}
                data-testid={`field-sheet-spec-${s.prefix}`}>Open</button>
            </div>
          ))}
          {sheet.specs.length ? null : <p className="kso-hint">No spec sheets on this sheet.</p>}
          {sheet.materials?.length ? (
            <div data-testid="field-sheet-materials">
              <div className="kfs-group">Materials on site · {sheet.materials.length}</div>
              {sheet.materials.map((m) => (
                <div className="kso-row" key={m.code} data-testid={`field-sheet-material-${m.code}`}>
                  <div className="t"><b>{m.title}</b>{m.spec_num ? ` · ${m.spec_num}` : ''}
                    <span className="src">{m.room}{m.location ? ` · ${m.location}` : ''} · {m.count_index} of {m.count_total}</span>
                  </div>
                  <span className={`kso-tag ${m.state === 'installed' ? 'ok' : 'wait'}`}>
                    {m.state === 'installed' ? 'installed' : 'on site'}
                  </span>
                  <a className="kso-btn" href={`/m/${m.code}`}
                    data-testid={`field-sheet-material-open-${m.code}`}>Open</a>
                </div>
              ))}
            </div>
          ) : null}
          {sheet.punch?.length ? (
            <div data-testid="field-sheet-punch">
              <div className="kfs-group">Punch list · {sheet.kind === 'room' ? sheet.room : sheet.trade} · {sheet.punch.length} open</div>
              {sheet.punch.map((p) => (
                <div className="kso-row" key={p.id}>
                  <div className="t"><b>{p.title}</b>
                    <span className="src">{[p.room, p.location].filter(Boolean).join(' · ')}</span>
                  </div>
                  <span className="kso-tag wait">open</span>
                </div>
              ))}
            </div>
          ) : null}
          {sheet.notes?.length ? (
            <div data-testid="field-sheet-notes">
              <div className="kfs-group">Notes for the field</div>
              {sheet.notes.map((n) => (
                <div className="kso-row" key={n.text + n.at}>
                  <div className="t"><b>{n.text}</b>
                    <span className="src">{sheet.firm} · {fmtDate(n.at)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          {specs ? (
            <div data-testid="field-sheet-spec-items">
              <div className="kfs-group">{specs.title}</div>
              {specs.items.map((item) => (
                <div className="kso-row" key={item.id}>
                  <div className="t"><b>{item.title || item.name}</b>
                    <span className="src">{[item.brand, item.model].filter(Boolean).join(' · ')}</span>
                  </div>
                  {item.image_url ? (
                    <a className="kso-btn" href={abs(item.image_url)} target="_blank" rel="noreferrer">Photo</a>
                  ) : null}
                </div>
              ))}
              {specs.items.length ? null : <p className="kso-hint">Nothing in this category yet.</p>}
            </div>
          ) : null}
        </div>
        <div className="kso-foot">Read-only · questions go to your foreman · {sheet.firm}</div>
      </div>
    </div>
  );
}
